<template>
  <div>
    <!-- Chat Pagination -->
    <chat-pagination
      v-if="chatDates && chatDates.length"
      :chatDates="chatDates"
      :openInModal="openInModal"
      :modalArchiveId="modalArchiveId"
      @updateModalArchiveId="updateModalArchiveId"
      :isActionDisabled="loadingChat"
    />
    <!-- Chat Box -->
    <div :class="{ 'data-tag mt-4 px-10 py-6': isAnnotationScreen }">
      <v-row
        ref="chatBox"
        class="chat-box pa-4 flex-column flex-nowrap mt-2 mb-6"
        id="chatBox"
        data-test-id="chat-box"
      >
        <div v-for="messageDetails in chatData" :key="messageDetails.id">
          <message
            :messageDetails="modifyMessageDetails(messageDetails)"
            :messageGroupType="messageGroupType"
            :chatAccessibleBy="chatAccessibleBy"
            @getChildMessages="getChildMessages"
          />
        </div>
        <message-skeleton v-if="loadingChat" :numberOfSkeletons="4" />
      </v-row>
    </div>
  </div>
</template>
<script>
const Message = () => import('@/components/ArchiveSearch/ChatBox/Message');
const MessageSkeleton = () =>
  import('@/components/ArchiveSearch/ChatBox/MessageSkeleton');
const ChatPagination = () =>
  import('@/components/ArchiveSearch/ChatBox/ChatPagination');
export default {
  name: 'chat-box',

  components: { Message, MessageSkeleton, ChatPagination },

  props: {
    propsMessageGroupId: String,
    openInModal: Boolean,
    modalArchiveId: String
  },

  data() {
    return {
      chatData: [],
      loadingChat: false,
      chatDates: [],
      pageNumber: 1,
      timeoutId: '',
      dateListPageNumber: 1,
      totalMessageCount: 0,
      threadDetails: {}
    };
  },

  computed: {
    archiveId() {
      return this.$route?.query?.archiveId || this.modalArchiveId || '';
    },
    isAnnotationScreen() {
      return Boolean(this.$route.params?.projectId);
    },
    messageGroupType() {
      return this.threadDetails?.messageGroupType || '';
    },
    chatAccessibleBy() {
      return this.threadDetails?.accessibleBy || [];
    }
  },

  watch: {
    archiveId() {
      this.getThreadMessages('newArchives');
    }
  },

  mounted() {
    this.$emit('toggleContentLoading', true);
    this.initInfiniteScroll();
    this.getAllThreads();
    this.getThreadMessages();
  },

  methods: {
    // To get all threads
    async getAllThreads() {
      try {
        const allThreadsData = await this.$services.vaultData.getAllThreads({
          pageNumber: this.dateListPageNumber,
          messageGroupId:
            this.$route.query?.messageGroupId || this.propsMessageGroupId || '',
          projectId: this.openInModal ? '' : this.$route.params?.projectId || ''
        });
        const allThreads = this.parseResponse(allThreadsData)?.results || [];
        this.chatDates = [
          ...this.chatDates,
          ...this.modifyDisplayChatDates(allThreads)
        ];
      } catch (error) {
        this.snackbar('error', error);
      }
    },
    modifyMessageDetails(messageDetails) {
      if (
        JSON.stringify(messageDetails?.attachments).includes('messageReference')
      ) {
        let messageAttachment = messageDetails?.attachments?.find(
          attachment => attachment.contentType === 'messageReference'
        );
        if (messageAttachment) {
          const {
            messagePreview,
            messageSender: { user }
          } = JSON.parse(messageAttachment.content);
          let messageModified = {
            attachments: [],
            messageId: messageDetails?.messageId || '',
            id: messageDetails?.id || '',
            ...{
              replies: {
                messages: [
                  {
                    ...messageDetails,
                    versions: messageDetails?.replies?.messages?.[0]?.versions
                  }
                ]
              },
              hasReplies: true,
              message: {
                content: messagePreview,
                contentType: 'html'
              },
              sender: user,
              senderName: user?.displayName
            }
          };

          delete messageModified.hasVersions;

          return messageModified;
        }
      } else {
        return messageDetails;
      }
    },

    /**
     * To get thread messages of selected date (or archive Id)
     * @param {String} get
     *
     * if get != newArchive, then operation = infintie scroll & append more data to existing chat data
     * else, set page number = 1 & replace chat data with new archiveId data
     */
    async getThreadMessages(get) {
      try {
        this.loadingChat = true;
        if (get === 'newArchives') {
          this.pageNumber = 1;
          this.chatData = [];
          // Scroll to top in chat-box for new archive data
          this.$refs.chatBox?.scrollTo(0, 0);
        }
        const threadDetailsData = await this.$services.vaultData.getThreadMessages(
          {
            pageNumber: this.pageNumber,
            archiveId: this.archiveId
          }
        );
        const threadDetails = this.parseResponse(threadDetailsData);
        this.threadDetails = threadDetails;
        const {
          name,
          accessibleBy,
          hotStoragePath,
          createdOn,
          totalMessageCount,
          messages
        } = threadDetails;
        this.totalMessageCount = totalMessageCount || 0;
        const modifiedMessages = this.modifyMessages(messages);
        if (get === 'newArchives') {
          this.chatData = modifiedMessages;
        } else {
          this.chatData = [...this.chatData, ...modifiedMessages];
        }

        // Passing data to set archive summary and Meta Data
        this.$emit('setMetaData', { name, accessibleBy });
        this.$emit('setArchiveSummaryReport', {
          sourcePathURL: hotStoragePath,
          createdAt: createdOn,
          sourceType: 'o365_teams',
          createdBy: accessibleBy,
          multipleCreators: true
        });
        this.$emit('initAnnotation');
      } catch (error) {
        this.snackbar('error', error);
        this.$router.go(-1);
      } finally {
        this.loadingChat = false;
        this.$emit('toggleContentLoading', false);
      }
    },

    // Implementing Infinite scroll to chat-box
    initInfiniteScroll() {
      const chatBox = document.querySelector('.chat-box');
      chatBox?.addEventListener('scroll', () => {
        clearTimeout(this.timeoutId);
        const fetchMoreMessages =
          this.totalMessageCount > this.chatData?.length;
        // this.timeoutId = setTimeout(() => {
        if (
          fetchMoreMessages &&
          !this.loadingChat &&
          Math.ceil(chatBox.scrollTop) + chatBox.clientHeight >=
            chatBox.scrollHeight
        ) {
          this.pageNumber++;
          this.getThreadMessages();
        }
        // eslint-disable-next-line
        // }, 100);
      });
    },

    // Get message replies on a particular message by messageId
    /**
     *
     * @param {String} messageId, parent message id
     * @param {Boolean} showMessageReply, if showMessageReply = true, show reply list, else version list
     * @param {Boolean} showReplyVersions, list of versions of reply messages
     * @param {String} replyId, messageId of reply message
     */
    async getChildMessages({
      messageId,
      showMessageReply,
      showReplyVersions,
      replyId
    }) {
      // Parent message Details against messageId
      const messageDetails = this.chatData.find(({ id }) => id === messageId);

      // Details of Reply of a message Details against replyId
      const messageReplyDetails = messageDetails?.replies?.messages?.find(
        ({ id }) => id === replyId
      );

      // List of replies/versions we want to get
      const messageType = showMessageReply ? 'replies' : 'versions';

      const alreadyFetchedChildMessagesForMessageId = (
        [messageReplyDetails || messageDetails]?.[messageType]?.messages || []
      ).length;
      // If replies / versions are already fetched earlier for that message id then don't fetch again same data
      // else fetch replies
      if (alreadyFetchedChildMessagesForMessageId) {
        return;
      }

      // For chats other than CHANNEL type
      if (
        this.threadDetails?.messageGroupType !== 'CHANNEL' &&
        showMessageReply
      ) {
        const filterRefereneMessage = messageDetails?.attachments
          ?.filter(({ contentType }) => contentType === 'messageReference')
          .map(({ content }) => JSON.parse(content));
        this.appendReplyToMessage(
          {
            totalMessageCount: filterRefereneMessage?.length,
            messages: this.modifyMessages(filterRefereneMessage)
          },
          messageId,
          showMessageReply,
          showReplyVersions,
          replyId
        );
      } else {
        try {
          const repliesData = await this.$services.vaultData.getChildMessages({
            archiveId: this.archiveId,
            messageId: showMessageReply
              ? messageId
              : (messageReplyDetails || messageDetails)?.messageId || '',
            messageType
          });
          let { totalMessageCount, messages } = this.parseResponse(repliesData);

          // If we need to show list of Versions
          // First filter out latesst version from the list
          // Second reverse the list in descending order of versioning (New first, Old last)
          if (!showMessageReply) {
            messages = messages.filter(
              ({ id }) => id !== (showReplyVersions ? replyId : messageId)
            );
            messages.reverse();
          }

          const modifiedMessages = this.modifyMessages(messages);
          this.appendReplyToMessage(
            {
              totalMessageCount,
              messages: modifiedMessages
            },
            messageId,
            showMessageReply,
            showReplyVersions,
            replyId
          );
        } catch (error) {
          this.snackbar('error', error);
        }
      }
      this.$emit('initAnnotation');
    },

    // Append Reply to the message
    appendReplyToMessage(
      replyDetails,
      messageId,
      showMessageReply,
      showReplyVersions,
      replyId
    ) {
      this.chatData = this.chatData.map(messageDetails => {
        const { id, replies } =
          this.threadDetails?.messageGroupType !== 'CHANNEL'
            ? this.modifyMessageDetails(messageDetails)
            : messageDetails;

        if (id === messageId) {
          // Iterating reply messages to embed versions of edited reply messages
          if (showReplyVersions) {
            // Append versions of reply messages
            const replyMessages = replies?.messages?.map(replyMesageDetails =>
              replyMesageDetails?.id === replyId
                ? { ...replyMesageDetails, versions: replyDetails }
                : replyMesageDetails
            );

            return {
              ...messageDetails,
              replies: {
                messages: replyMessages,
                totalMessageCount: replies?.totalMessageCount
              }
            };
          }

          return {
            ...messageDetails,
            [showMessageReply ? 'replies' : 'versions']: replyDetails || {}
          };
        }
        return messageDetails;
      });
    },

    // Modify messages
    modifyMessages(messages) {
      return (messages || []).map(messageDetails => {
        const senderName = this.getMessageSenderName(
          messageDetails?.sender || {}
        );
        return {
          ...messageDetails,
          senderName
        };
      });
    },

    modifyReferenceMessages(messages) {
      return (messages || []).map(messageDetails => {
        const { name, userId } = messageDetails?.sender || {};
        const isExternalUser = messageDetails?.sender?.external;
        let senderName = '';
        if (isExternalUser) {
          senderName = name || 'External User';
        }
        senderName =
          this.formatUserName(this.userIdNameMap[userId]) || name || '';
        return {
          ...messageDetails,
          senderName
        };
      });
    },

    // Update chatDates by adding displayDate to each timestamp
    modifyDisplayChatDates(allThreads) {
      return allThreads.map(threadData => {
        return {
          archiveId: threadData?.archiveId || '',
          displayDate: `${this.getFormatedDateAndTime(
            threadData?.source?.startedOn
          )} - ${this.getFormatedDateAndTime(threadData?.source?.endedOn)} `
        };
      });
    },

    updateModalArchiveId(archiveId) {
      this.$emit('updateModalArchiveId', archiveId);
    }
  }
};
</script>
<style scoped>
.chat-box {
  border: 1px solid #eee;
  max-height: 500px;
  overflow-y: scroll;
}
</style>
